import { ApolloError, useMutation } from '@apollo/client';
import { assignDeviceMutation, partnerAddDeviceToInventoryMutation } from '../lib/rescueQueries';

interface AssignArgs {
  moduleId: string;
  petId: string;
  /** The collar isn't on the partner's books yet: add it to them before assigning. */
  addToInventory?: boolean;
}

/** Assigns a collar to a pet, first adding it to the partner's inventory when asked. */
export function useAssignDevice() {
  const [assignDevice, { loading: assigning }] = useMutation(assignDeviceMutation, {
    refetchQueries: 'active' as const,
  });
  const [addToInventory, { loading: adding }] = useMutation(partnerAddDeviceToInventoryMutation, {
    refetchQueries: 'active' as const,
  });

  async function assign({ moduleId, petId, addToInventory: addFirst = false }: AssignArgs) {
    if (!addFirst) {
      await assignDevice({ variables: { moduleId, petId } });
      return;
    }
    try {
      await addToInventory({ variables: { input: { moduleId } } });
    } catch (err) {
      // The API refuses with a bare Forbidden, so the books can't be probed; only a lost race gets here.
      if (err instanceof ApolloError && err.graphQLErrors.length) {
        throw new Error(`Device ${moduleId} can't be added to your inventory. Contact Fi support.`);
      }
      throw err;
    }
    try {
      await assignDevice({ variables: { moduleId, petId } });
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'unknown error';
      throw new Error(
        `Device ${moduleId} was added to your inventory but couldn't be assigned: ${detail}. Assign it from your stock.`,
      );
    }
  }

  return { assign, assigning: assigning || adding };
}
